import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, SplitLines } from "./Reveal";
import { ZoomImage } from "./Lightbox";
import { Sparkles } from "./Sparkles";

const Frame = ({ src, alt, className, caption, clip, testId }) => (
  <motion.figure
    data-testid={testId}
    initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
    className={`relative overflow-hidden group ${className}`}
  >
    <div className="absolute inset-0 overflow-hidden img-frame" style={{ clipPath: clip }}>
      <ZoomImage src={src} alt={alt} className="h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-105" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_35%,rgba(11,11,11,0.75)_100%)] pointer-events-none" />
      <Sparkles count={6} />
    </div>
    <figcaption className="absolute bottom-4 left-4 text-[10px] uppercase tracking-[0.3em] text-ivory/80 bg-night/60 backdrop-blur px-3 py-1.5 border border-gold/20 pointer-events-none">{caption}</figcaption>
  </motion.figure>
);

const PILLARS = [
  { tag: "For jewellers · B2B", title: "Wholesale parcels, calibrated by sieve.", points: ["Calibrated melee -2 to +15 sieve", "Colour-matched parcels for production", "GST invoice · pan-India dispatch", "Standing orders & credit terms for regulars"], to: "/bulk-order", cta: "Bulk order" },
  { tag: "For you · B2C", title: "One certified stone, chosen with care.", points: ["IGI-certified solitaires 0.30 – 5 ct for rings, studs & pendants", "Try stones on your hand under lamp and loupe at Abids", "Custom setting in 14k / 18k gold, made to your design", "Honest guidance — no showroom pressure"], to: "/enquiry?buyer=B2C", cta: "Find my stone" },
];

export const Editorial = () => (
  <section id="collection" data-testid="editorial-section" className="relative py-24 lg:py-36 bg-[#0d0d0d] border-y border-gold/10 grain">
    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-6 lg:gap-8 mb-24">
        <Frame testId="editorial-img-model" src="/img/model.jpg" alt="Model wearing lab grown diamond drop earrings and a lab grown diamond necklace" className="lg:col-span-5 aspect-[3/4]" caption="Drop earrings · 2 × 0.50 ct pear" clip="polygon(0 0, 100% 0, 100% 100%, 0 92%)" />
        <div className="lg:col-span-7 grid grid-rows-[auto_1fr] gap-6 lg:gap-8">
          <div className="grid sm:grid-cols-2 gap-6 lg:gap-8">
            <Frame testId="editorial-img-parcel" src="/img/parcel.jpg" alt="Wholesale lab grown melee diamond parcel, +4 to -6.5 sieve, with tweezers" className="aspect-[4/3]" caption="+4 to -6.5 sieve parcel" clip="polygon(0 8%, 100% 0, 100% 100%, 0 100%)" />
            <Frame testId="editorial-img-loupe" src="/img/loupe.jpg" alt="Grading a VVS clarity lab grown diamond under a 10x jeweller's loupe" className="aspect-[4/3]" caption="10× loupe grading" clip="polygon(0 0, 100% 0, 100% 100%, 8% 100%)" />
          </div>
          <Frame testId="editorial-img-ring" src="/img/ring.jpg" alt="Emerald cut lab grown diamond engagement ring in 18k yellow gold" className="min-h-[300px]" caption="Emerald cut · 1.50 ct · 18k" clip="polygon(0 0, 92% 0, 100% 12%, 100% 100%, 0 100%)" />
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <Reveal><p className="eyebrow mb-6">02 — Who we serve</p></Reveal>
          <h2 className="font-display font-light text-ivory text-[clamp(2.4rem,5vw,4.5rem)] leading-[0.95]">
            <SplitLines inView lines={["Trade", <span key="a" className="text-saffron italic">and treasured.</span>]} />
          </h2>
        </div>
        <div className="lg:col-span-8 grid md:grid-cols-2 gap-6">
          {PILLARS.map((p, i) => (
            <Reveal key={p.tag} delay={i * 0.12} className="relative bg-night border border-gold/20 p-8 hover:border-saffron/60 transition-colors duration-500" data-testid={`pillar-${i}`}>
              <p className="eyebrow">{p.tag}</p>
              <h3 className="font-display text-3xl text-ivory mt-4 leading-tight">{p.title}</h3>
              <ul className="mt-6 space-y-3">
                {p.points.map((pt) => <li key={pt} className="flex gap-3 text-ivory/70 font-light text-sm"><span className="mt-2 h-1 w-1 rotate-45 bg-saffron shrink-0" />{pt}</li>)}
              </ul>
              <Link to={p.to} data-testid={`pillar-cta-${i}`} className="btn-outline mt-8 !text-[11px] !py-2.5">{p.cta}</Link>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);
